import { useMemo } from "react";
import type { IconName } from "@salestrak/ui";
import {
  SCREEN_HOTKEYS,
  TILES,
  resolveModuleRoute,
} from "#app/components/navigation/module-rail/tiles";
import {
  ADMINISTRATION_ITEMS,
  FEATURE_ITEMS,
  USER_SETTINGS,
  isNavItemVisible,
} from "#app/components/navigation/settings-nav-items";
import { useAppData } from "#app/lib/data";
import {
  checkPermission,
  useViewerCanBrowseMembers,
} from "#app/lib/hooks/use-permission";
import { useWorkflowsLanding } from "#app/lib/hooks/use-workflows-landing";
import type { Omittable } from "@salestrak/ui";

// One flat "Commands" list, in hand-picked order (never re-sorted). The verb
// is the contract: "Go to" NAVIGATES to a screen (module, page, settings
// page) and "Open" raises a dialog over the current screen. Creation leads,
// then every destination, then the dialogs, utilities last. Labels are Title
// Case except connective words ("to", "a").
export type CommandDef = Omittable<{
  id: string;
  icon: IconName;
  label: string;
  keywords?: string[];
  action: () => void;
  /** A route or "action" — never rendered in the row (a "/" query finds
   *  commands by name). A route labels the footer "Navigate" instead of
   *  "Command". */
  meta?: string;
  /** Second key of the destination's `G` chord, shown at rest as the row's
   *  trailing hint — the same shortcut the rail tooltip advertises. */
  hotkey?: string;
}>;

// The "Go to" set is DERIVED, never hand-listed: module tiles and `G`-chord
// screens come from the rail registry (so the command lands exactly where the
// tile and the chord do), and settings pages come from the sidebar's arrays
// (so a page reaches the search with the sidebar's own permission gate) —
// only the pages that opted in with a `commandLabel`, so the list stays short.
function useGoToCommands(navigate: (route: string) => void): CommandDef[] {
  const { currentUser } = useAppData();
  const workflowId = useWorkflowsLanding();
  const canBrowseMembers = useViewerCanBrowseMembers();
  const permissions = currentUser?.permissions;
  return useMemo((): CommandDef[] => {
    const has = (key: string) => checkPermission(permissions, key);
    const modules = TILES.map(
      (tile): CommandDef => ({
        id: `go-${tile.id}`,
        icon: tile.icon,
        label: `Go to ${tile.label}`,
        keywords: [tile.label.toLowerCase(), "go", "go to"],
        action: () =>
          navigate(resolveModuleRoute(tile, { workflowId })),
        meta: tile.route,
        hotkey: tile.hotkey,
      }),
    );
    const screens = SCREEN_HOTKEYS.map(
      (screen): CommandDef => ({
        id: `go-${screen.id}`,
        icon: screen.icon,
        label: `Go to ${screen.label}`,
        keywords: [screen.label.toLowerCase(), "go", "go to"],
        action: () => navigate(screen.route),
        meta: screen.route,
        hotkey: screen.hotkey,
      }),
    );
    // The members roster is a Teams-module screen with no chord of its own.
    // A member surface — withheld from a share-only viewer like the sidebar
    // row and the route itself.
    const members: CommandDef[] = canBrowseMembers
      ? [
          {
            id: "go-members",
            icon: "square-user-round",
            label: "Go to Members",
            keywords: ["members", "people", "roster", "go", "go to"],
            action: () => navigate("/members"),
            meta: "/members",
          },
        ]
      : [];
    const settingsPages = [
      ...USER_SETTINGS,
      ...FEATURE_ITEMS,
      ...ADMINISTRATION_ITEMS,
    ].flatMap((item): CommandDef[] =>
      item.commandLabel && isNavItemVisible(item, has)
        ? [
            {
              id: `go-settings-${item.route}`,
              icon: item.icon,
              label: item.commandLabel,
              keywords: [item.label.toLowerCase(), "settings", "go", "go to"],
              action: () => navigate(item.route),
              meta: item.route,
            },
          ]
        : [],
    );
    return [...modules, ...screens, ...members, ...settingsPages];
  }, [navigate, permissions, workflowId, canBrowseMembers]);
}

// `openCreateIssue` is absent on surfaces with no create affordance, and the
// command list already reads it as a truthiness check — so the member relaxes.
// The three manager-dialog openers are absent exactly when their door is
// hidden elsewhere (useManageDialogDoors) — same truthiness contract.
export function useCommands(opts: Omittable<{
  navigate: (route: string) => void;
  toggleTheme: () => void;
  openFeedback: () => void;
  openCreateIssue?: () => void;
  openCustomFields?: () => void;
  openAutomations?: () => void;
  openAppCenter?: () => void;
  themeLabel: string;
}>): CommandDef[] {
  const {
    navigate,
    openCreateIssue,
    openCustomFields,
    openAutomations,
    openAppCenter,
    toggleTheme,
    openFeedback,
    themeLabel,
  } = opts;
  const goTo = useGoToCommands(navigate);
  return useMemo(
    (): CommandDef[] => [
      ...(openCreateIssue
        ? [
            {
              id: "create-issue",
              icon: "plus" as const,
              label: "Create New Project",
              keywords: ["create", "new", "project"],
              action: openCreateIssue,
            },
          ]
        : []),
      ...goTo,
      // The route-less manager dialogs. Labels match the settings sidebar
      // rows; the workspace menu's shorter names ride as keywords.
      ...(openCustomFields
        ? [
            {
              id: "open-custom-fields",
              icon: "swatch-book" as const,
              label: "Open Custom Field Manager",
              keywords: ["custom", "fields", "field", "manager", "open"],
              action: openCustomFields,
              meta: "action",
            },
          ]
        : []),
      ...(openAutomations
        ? [
            {
              id: "open-automations",
              icon: "zap" as const,
              label: "Open Automations Manager",
              keywords: ["automations", "automation", "manager", "open"],
              action: openAutomations,
              meta: "action",
            },
          ]
        : []),
      ...(openAppCenter
        ? [
            {
              id: "open-app-center",
              icon: "puzzle" as const,
              label: "Open App Center",
              keywords: ["app", "apps", "center", "integrations", "open"],
              action: openAppCenter,
              meta: "action",
            },
          ]
        : []),
      {
        id: "toggle-theme",
        icon: "sun-moon",
        label: themeLabel,
        keywords: ["theme", "dark", "light", "mode", "toggle"],
        action: toggleTheme,
        meta: "action",
      },
      {
        id: "report-bug",
        icon: "message",
        label: "Report a Bug",
        keywords: ["bug", "feedback", "report"],
        action: openFeedback,
        meta: "action",
      },
    ],
    [
      goTo,
      openCreateIssue,
      openCustomFields,
      openAutomations,
      openAppCenter,
      toggleTheme,
      openFeedback,
      themeLabel,
    ],
  );
}

export function filterCommands(commands: CommandDef[], query: string): CommandDef[] {
  if (!query.trim()) return commands;
  const lower = query.toLowerCase();
  return commands.filter(
    (cmd) =>
      cmd.label.toLowerCase().includes(lower) ||
      cmd.keywords?.some((kw) => kw.includes(lower)),
  );
}
